import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a number with comma separators for better readability
 * @param value - The number to format (can be number or string)
 * @param decimals - Number of decimal places (default: 0 for whole numbers, 2 for decimals)
 * @returns Formatted number string with commas (e.g., "2,700" or "10,000")
 */
export function formatNumber(value: number | string | null | undefined, decimals?: number): string {
  if (value === null || value === undefined || value === '') {
    return '0';
  }

  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(num)) {
    return '0';
  }

  // Auto-detect decimals if not specified
  const hasDecimals = num % 1 !== 0;
  const decimalPlaces = decimals !== undefined ? decimals : (hasDecimals ? 2 : 0);

  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
}

/**
 * Converts currency codes to symbols
 * @param currency - Currency code (e.g., 'USD', 'EUR') or null/undefined
 * @returns Currency symbol (e.g., '$', '€')
 */
export function getCurrencySymbol(currency?: string | null): string {
  if (!currency) return '$';
  const code = currency.toUpperCase();
  switch (code) {
    case 'USD': return '$';
    case 'EUR': return '€';
    case 'GBP': return '£';
    case 'JPY': return '¥';
    case 'CNY': return '¥';
    case 'INR': return '₹';
    case 'AUD': return 'A$';
    case 'CAD': return 'C$';
    default: return currency;
  }
}

/**
 * Formats a price with currency symbol and comma separators
 * @param value - The price to format
 * @param currency - Currency symbol (default: '$')
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted price string (e.g., "$2,700" or "$10,000")
 */
export function formatPrice(value: number | string | null | undefined, currency: string = '$', decimals: number = 0): string {
  if (value === null || value === undefined || value === '') {
    return `${currency}0`;
  }

  return `${currency}${formatNumber(value, decimals)}`;
}
